import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Save, Lock } from "lucide-react";
import { toast } from "sonner";
import { TeachersManager } from "./TeachersManager";
import { MidRecital, LinkedShowDialog } from "./RecitalFields";

const put = (year, r, actions = {}) => api.put("/admin/course-meta", {
  course: r.course, teacher_name: r.teacher_name || "", recital_date: r.recital_date || "", teacher_user_id: r.teacher_user_id || null,
  mid_recital_date: r.mid_recital_date || "", linked_actions: actions,
}, { params: { year } });
const linkedConflict = (err) => (err.response?.status === 409 && err.response.data?.detail?.code === "linked_show_modified" ? err.response.data.detail : null);

export const CourseMetaSection = forwardRef(({ year, courses }, ref) => {
  const [rows, setRows] = useState(null);
  const [dirty, setDirty] = useState(new Set());
  const [saving, setSaving] = useState(null);
  const [staff, setStaff] = useState({ teachers: [], master: null });
  const [conflict, setConflict] = useState(null);
  const [conflictOpen, setConflictOpen] = useState(false);
  const openConflict = (c) => { setConflict(c); setConflictOpen(true); };

  useEffect(() => {
    api.get("/admin/teachers").then((r) => setStaff(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    setRows(null); setDirty(new Set());
    if (!year) return undefined;
    let stale = false;  // una risposta arrivata tardi non sovrascrive le modifiche
    api.get("/admin/course-meta", { params: { year } }).then((r) => { if (!stale) setRows(r.data); }).catch(() => { if (!stale) setRows([]); });
    return () => { stale = true; };
  }, [year, courses.join("|")]); // eslint-disable-line react-hooks/exhaustive-deps

  const upd = (course, patch) => {
    setRows((rs) => rs.map((r) => (r.course === course ? { ...r, ...patch } : r)));
    setDirty((d) => new Set(d).add(course));
  };
  const clean = (course) => setDirty((d) => { const n = new Set(d); n.delete(course); return n; });

  // Account selezionabili: master + docenti nominati
  const accounts = [...(staff.master ? [{ ...staff.master, name: `${staff.master.name} (master)` }] : []), ...staff.teachers];
  const pickAccount = (course, id) => {
    const a = accounts.find((x) => x.user_id === id);
    upd(course, id === "none" ? { teacher_user_id: null } : { teacher_user_id: id, teacher_name: (a?.name || "").replace(/ \(master\)$/, "") });
  };
  const setTeachers = (teachers) => {
    setStaff((s) => ({ ...s, teachers }));
    // revoca: il server scollega i corsi, qui allineo le righe
    const ids = new Set([...(staff.master ? [staff.master.user_id] : []), ...teachers.map((t) => t.user_id)]);
    setRows((rs) => rs?.map((r) => (r.teacher_user_id && !ids.has(r.teacher_user_id) ? { ...r, teacher_user_id: null } : r)));
  };

  // Usato dal tasto "Salva anno e corsi": salva le righe modificate
  useImperativeHandle(ref, () => ({
    saveDirty: async () => {
      for (const r of (rows || []).filter((x) => dirty.has(x.course))) {
        try { await put(year, r); clean(r.course); }
        catch (err) {
          const c = linkedConflict(err);
          if (!c) throw err;
          openConflict({ ...c, row: r });
          throw new Error("Scegli cosa fare dello spettacolo collegato al saggio, poi salva di nuovo.");
        }
      }
    },
  }), [rows, dirty, year]);

  const save = async (r, actions) => {
    setSaving(r.course);
    try { await put(year, r, actions); clean(r.course); toast.success("Salvato"); }
    catch (err) {
      const c = linkedConflict(err);
      if (c) openConflict({ ...c, row: r });
      else toast.error(apiError(err.response?.data?.detail));
    } finally { setSaving(null); }
  };
  const choose = (action) => {
    const c = conflict;
    setConflictOpen(false);
    save(c.row, Object.fromEntries(c.shows.map((s) => [s.kind, action])));
  };

  return (
    <Card className="p-6 bg-card border-border space-y-4" data-testid="course-meta-section">
      <h3 className="text-xl">Docenti e saggi</h3>
      <TeachersManager teachers={staff.teachers} onChange={setTeachers} />
      <p className="text-sm text-muted-foreground">Per ogni corso salvato: docente (un solo account per corso) e date dei saggi. Il calendario lezioni viene generato fino al saggio di fine anno. Per ogni data di saggio viene creato uno spettacolo nascosto in "Spettacoli", dove inserisci titolo, locandina, luogo, orari, biglietti e note. Puoi salvare la singola riga o tutto con "Salva anno e corsi" in fondo.</p>
      {rows === null ? <p className="text-sm text-muted-foreground">Caricamento…</p> : rows.length === 0 ? <p className="text-sm text-muted-foreground">Nessun corso salvato per quest'anno.</p> : (
        <div className="space-y-3">
          {rows.map((r, i) => (
            <div key={r.course} data-testid={`course-meta-${i}`} className="rounded-lg border border-border p-3 space-y-2">
              <p className="text-sm font-medium text-foreground break-words">{r.course}
                {r.calendar_locked && <span className="ml-2 inline-flex items-center gap-1 text-xs text-foreground" data-testid={`course-meta-locked-${i}`}><Lock size={12} /> calendario bloccato</span>}
                {dirty.has(r.course) && <span className="ml-2 text-xs text-amber-700" data-testid={`course-meta-dirty-${i}`}>· modifiche non salvate</span>}</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-[1.3fr_1.3fr_1fr_auto] gap-2 items-end">
                <div><Label className="text-xs">Account docente</Label>
                  <Select value={r.teacher_user_id || "none"} onValueChange={(v) => pickAccount(r.course, v)}>
                    <SelectTrigger data-testid={`course-meta-account-${i}`} className="mt-1 bg-white h-auto min-h-10 text-left whitespace-normal"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nessun account (solo nome)</SelectItem>
                      {accounts.map((a) => <SelectItem key={a.user_id} value={a.user_id}>{a.name || a.email}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Nome del docente</Label><Input data-testid={`course-meta-teacher-${i}`} value={r.teacher_name || ""} onChange={(e) => upd(r.course, { teacher_name: e.target.value })} placeholder="Nome e cognome" className="mt-1 bg-white" /></div>
                <div><Label className="text-xs">Saggio di fine anno</Label><Input data-testid={`course-meta-recital-${i}`} type="date" value={r.recital_date || ""} onChange={(e) => upd(r.course, { recital_date: e.target.value })} className="mt-1 bg-white" /></div>
                <Button size="sm" data-testid={`course-meta-save-${i}`} onClick={() => save(r)} disabled={saving === r.course} variant="outline" className="h-10"><Save size={14} className="mr-1" /> Salva</Button>
              </div>
              <MidRecital r={r} i={i} upd={upd} unsaved={dirty.size > 0} />
            </div>
          ))}
        </div>
      )}
      <LinkedShowDialog open={conflictOpen} conflict={conflict} onChoose={choose} onCancel={() => setConflictOpen(false)} />
    </Card>
  );
});
CourseMetaSection.displayName = "CourseMetaSection";
